/**
 * EmailLinkTickCross — qb!3096 operator confirmation strip.
 *
 * Rendered in the Card detail drawer's Email tab whenever a card
 * has been autonomously linked to an Email row by the deterministic
 * matcher or the DeepSeek second-pass scorer. Two buttons:
 *
 *   - Tick   → POST /api/trello/cards/<ext>/email/confirm/
 *   - Cross  → POST /api/trello/cards/<ext>/email/reject/
 *              clears `Card.primary_email_id` AND records a
 *              `rejected` decision row keyed on (card, email).
 *              The retraining loop:
 *                · the deterministic linker skips (card, email)
 *                  for ever, AND
 *                · the DeepSeek second-pass uses the rejection
 *                  as a negative few-shot in subsequent prompts
 *                  for the same organisation.
 *
 * Below the buttons we render the operator's prior decisions on
 * this card (tick / cross history) so they can see why the link
 * looks the way it does without leaving the drawer.
 *
 * Failure mode: if the BE returns 409 (the supplied email_id no
 * longer matches the card's current primary_email_id — race
 * against a re-linker pass), we show the BE's detail message
 * inline, refetch the detail, and let the operator try again.
 *
 * Optional callbacks: the parent passes `confirm` / `reject` only
 * when the tenant's KanbanApi wires them (Trello-aware tenants
 * only). When undefined the buttons are hidden and we render
 * read-only history.
 *
 * Stack 12-style "lift discipline": this lives in `packages/ui`
 * from day one so any app that mounts `<CardDetailDrawer>`
 * inherits the tick/cross UI without per-app wiring.
 */

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { Check, History, X } from 'lucide-react'
import { useState } from 'react'

import type { TrelloEmailLinkDetail } from '../types'

export interface EmailLinkTickCrossProps {
  externalId: string
  emailLink: TrelloEmailLinkDetail
  /**
   * When the host wires the confirm endpoint via the tenant's
   * KanbanApi. Promise resolves to the backend decision result
   * envelope; we use it for the success toast / button reset but
   * the cache invalidation already covers the data refresh.
   */
  onConfirm?: (
    externalId: string,
    emailId?: string | null,
  ) => Promise<unknown>
  onReject?: (
    externalId: string,
    emailId?: string | null,
  ) => Promise<unknown>
}

const VIA_LABEL: Record<string, string> = {
  matched: 'deterministic',
  matched_metadata_only: 'metadata-only',
  matched_deepseek: 'DeepSeek',
  '': 'manual',
}

function viaPathLabel(via: string | null | undefined): string {
  if (!via) return ''
  return VIA_LABEL[via] ?? via
}

export function EmailLinkTickCross({
  externalId,
  emailLink,
  onConfirm,
  onReject,
}: EmailLinkTickCrossProps) {
  const qc = useQueryClient()
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const invalidate = () => {
    // Same key as `<TrelloCardDetailSection>` + `<EmailFromCard>`
    // so the drawer refetches the email_link block, the BE
    // primary_email_id, and the synthesised view in one shot.
    qc.invalidateQueries({ queryKey: ['trello-card-detail', externalId] })
  }

  const confirmMutation = useMutation({
    mutationFn: async () => {
      if (!onConfirm) throw new Error('confirm action not wired')
      return onConfirm(externalId, emailLink.current_email_id)
    },
    onSuccess: () => {
      setErrorMessage(null)
      invalidate()
    },
    onError: (err: unknown) => {
      setErrorMessage(err instanceof Error ? err.message : String(err))
    },
  })

  const rejectMutation = useMutation({
    mutationFn: async () => {
      if (!onReject) throw new Error('reject action not wired')
      return onReject(externalId, emailLink.current_email_id)
    },
    onSuccess: () => {
      setErrorMessage(null)
      invalidate()
    },
    onError: (err: unknown) => {
      setErrorMessage(err instanceof Error ? err.message : String(err))
    },
  })

  const busy = confirmMutation.isPending || rejectMutation.isPending
  const showActions = Boolean(emailLink.can_confirm || emailLink.can_reject)
  const recentDecisions = (emailLink.decisions ?? [])
    .filter((d) => d.superseded_at === null)
    .slice(0, 3)

  // The "current" decision (if any) for this email_id, so we can
  // visually pre-fill the active state when the operator has
  // already ticked this link.
  const activeDecisionForCurrent = emailLink.current_email_id
    ? (emailLink.decisions ?? []).find(
        (d) =>
          d.email_id === emailLink.current_email_id && d.superseded_at === null,
      )
    : undefined

  const tickActive = activeDecisionForCurrent?.decision === 'confirmed'

  return (
    <div
      data-testid="email-link-tick-cross"
      className="border-b border-amber-200 bg-amber-50 px-3 py-2 dark:border-amber-900/60 dark:bg-amber-950/30"
      role="region"
      aria-label="Operator decision on auto-linked email"
    >
      <div className="flex items-center justify-between gap-3">
        <div className="flex flex-col gap-0.5">
          <p className="text-[11.5px] font-medium text-amber-900 dark:text-amber-200">
            {tickActive
              ? 'You confirmed this email link.'
              : emailLink.current_email_id
                ? 'Is this the right email for this card?'
                : 'No email is linked to this card yet.'}
          </p>
          <p className="text-[10.5px] text-amber-800/80 dark:text-amber-300/70">
            {tickActive
              ? 'The matcher will treat this pair as canonical going forward.'
              : 'Tick teaches the matcher you agree; cross teaches it to never re-link this email here.'}
          </p>
        </div>

        {showActions && (
          <div className="flex shrink-0 items-center gap-1">
            {emailLink.can_confirm && (
              <button
                type="button"
                aria-label="Confirm email link"
                disabled={busy || !onConfirm}
                onClick={() => confirmMutation.mutate()}
                className={[
                  'inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition-colors',
                  tickActive
                    ? 'bg-emerald-600 text-white hover:bg-emerald-700'
                    : 'bg-white text-emerald-700 ring-1 ring-emerald-300 hover:bg-emerald-50 dark:bg-zinc-900 dark:text-emerald-300 dark:ring-emerald-700/50 dark:hover:bg-zinc-800',
                  busy ? 'opacity-50' : '',
                ].join(' ')}
              >
                <Check className="h-3.5 w-3.5" />
                Confirm
              </button>
            )}
            {emailLink.can_reject && (
              <button
                type="button"
                aria-label="Reject email link"
                disabled={busy || !onReject}
                onClick={() => rejectMutation.mutate()}
                className={[
                  'inline-flex items-center gap-1 rounded px-2 py-1 text-[11px] font-medium transition-colors',
                  'bg-white text-rose-700 ring-1 ring-rose-300 hover:bg-rose-50 dark:bg-zinc-900 dark:text-rose-300 dark:ring-rose-700/50 dark:hover:bg-zinc-800',
                  busy ? 'opacity-50' : '',
                ].join(' ')}
              >
                <X className="h-3.5 w-3.5" />
                Reject
              </button>
            )}
          </div>
        )}
      </div>

      {errorMessage && (
        <div className="mt-2 rounded border border-rose-300 bg-rose-50 px-2 py-1 text-[10.5px] text-rose-800 dark:border-rose-900/60 dark:bg-rose-950/40 dark:text-rose-300">
          {errorMessage}
        </div>
      )}

      {recentDecisions.length > 0 && (
        <details className="mt-2 text-[10.5px]" data-testid="email-link-decision-history">
          <summary className="inline-flex cursor-pointer items-center gap-1 text-amber-800 hover:text-amber-900 dark:text-amber-300 dark:hover:text-amber-200">
            <History className="h-3 w-3" />
            {recentDecisions.length} prior decision
            {recentDecisions.length === 1 ? '' : 's'}
          </summary>
          <ul className="mt-1 space-y-0.5 pl-4 text-amber-800/90 dark:text-amber-300/80">
            {recentDecisions.map((d) => (
              <li key={d.id} className="flex items-center gap-1.5">
                {d.decision === 'confirmed' ? (
                  <Check className="h-3 w-3 text-emerald-600" aria-hidden />
                ) : (
                  <X className="h-3 w-3 text-rose-600" aria-hidden />
                )}
                <span>
                  {d.decision === 'confirmed' ? 'confirmed' : 'rejected'}
                  {d.via_path ? ` (was via ${viaPathLabel(d.via_path)})` : ''}
                  {d.decided_at
                    ? ` · ${new Date(d.decided_at).toLocaleString()}`
                    : ''}
                </span>
              </li>
            ))}
          </ul>
        </details>
      )}
    </div>
  )
}
